import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, Contact, api, getHost, getToken, parseTags } from "../api";
import TagChips from "../components/TagChips";

export default function Contacts() {
  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [uploadResult, setUploadResult] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    const params = new URLSearchParams();
    params.set("limit", "100");
    if (search) params.set("q", search);
    if (tagFilter) params.set("tag", tagFilter);
    const r = await api<{ contacts: Contact[]; total: number; all_tags: string[] }>(
      `/api/contacts?${params}`,
    );
    setContacts(r.contacts);
    setTotal(r.total);
    setAllTags(r.all_tags ?? []);
  };

  useEffect(() => {
    load().catch((e) => alert(e.message));
  }, [search, tagFilter]);

  const upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source_label", file.name);
      const r = await fetch(`${getHost()}/api/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const text = await r.text();
      if (!r.ok) throw new ApiError(r.status, text);
      const j = JSON.parse(text);
      setUploadResult(
        `Imported ${j.inserted} new (${j.skipped_dup} duplicates, ${j.skipped_no_email} skipped: no email) from ${j.total_rows} rows in ${j.source}.`,
      );
      load();
    } catch (e) {
      setUploadResult(e instanceof ApiError ? `${e.status} ${e.body.slice(0, 200)}` : (e as Error).message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Contacts</h1>
          <p className="text-sm text-ink-500 mt-1">{total.toLocaleString()} match current filter</p>
        </div>
        <label className={`btn-primary cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}>
          {busy ? "uploading…" : "+ Upload CSV"}
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={upload} />
        </label>
      </div>

      {uploadResult && (
        <div className="card p-3 mb-4 text-sm font-mono text-ink-700 bg-ink-100 border-ink-300">
          {uploadResult}
        </div>
      )}

      <div className="space-y-2 mb-4">
        <input
          className="input"
          placeholder="Search email, name, category, notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {allTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-xs text-ink-500 mr-1">Filter by tag:</span>
            <button
              onClick={() => setTagFilter("")}
              className={`px-2 py-0.5 text-xs rounded-md border transition-colors ${
                tagFilter === "" ? "bg-ink-900 text-white border-ink-900" : "bg-white text-ink-700 border-ink-200 hover:bg-ink-50"
              }`}
            >
              all
            </button>
            {allTags.map((t) => (
              <button
                key={t}
                onClick={() => setTagFilter(t === tagFilter ? "" : t)}
                className={`px-2 py-0.5 text-xs rounded-md border transition-colors ${
                  t === tagFilter ? "bg-ink-900 text-white border-ink-900" : "bg-white text-ink-700 border-ink-200 hover:bg-ink-50"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {!contacts ? (
        <div className="text-sm text-ink-500">loading…</div>
      ) : contacts.length === 0 ? (
        <div className="card p-10 text-center text-sm text-ink-500">
          No contacts. Upload a CSV to get started.
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b border-ink-200">
              <tr className="text-left text-ink-500 text-xs uppercase tracking-wide">
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Category</th>
                <th className="px-3 py-2 font-medium">Tags</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {contacts.map((c) => (
                <tr key={c.id} className="hover:bg-ink-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link to={`/contacts/${c.id}`} className="hover:underline">{c.email}</Link>
                    {c.notes && <div className="text-ink-400 mt-0.5 truncate max-w-xs italic" title={c.notes}>📝 {c.notes.slice(0, 60)}</div>}
                  </td>
                  <td className="px-3 py-2">{c.name ?? "—"}</td>
                  <td className="px-3 py-2 text-ink-500">{c.category ?? "—"}</td>
                  <td className="px-3 py-2">
                    <TagChips value={parseTags(c.tags)} readOnly />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
